import { useCallback, useEffect, useState } from "react";
import { CashDebtsAPI, CashDebtsAdminAPI, MyCashDebt, StaffCashDebt } from "../api/cashDebts";
import { UsersAPI, UserDto } from "../api/usersApi";

interface Props { user: any }

const money = (n: number) => new Intl.NumberFormat("ru-RU").format(Math.round(n)) + " ₸";
const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
const days = (iso?: string | null) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0);

const card: React.CSSProperties = { background: "#11151f", border: "1px solid #1f2937", borderRadius: 12, padding: "14px 16px" };
const btn: React.CSSProperties = { padding: "8px 16px", borderRadius: 8, border: "none", background: "#10b981", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 };

/** Техник видит свой долг; руководитель, бухгалтер и админ принимают наличные */
export default function CashDebtsPage({ user }: Props) {
  const canSettle = ["ADMIN", "OFFICE_LEAD", "ACCOUNTANT"].includes(user?.role);
  const seesAll = canSettle || user?.role === "SENIOR_OFFICE";
  const [mine, setMine] = useState<MyCashDebt | null>(null);
  const [staff, setStaff] = useState<{ grandTotal: number; staff: StaffCashDebt[] } | null>(null);
  const [openUser, setOpenUser] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState<UserDto[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [fUser, setFUser] = useState<number | "">("");
  const [fAmount, setFAmount] = useState("");
  const [fNote, setFNote] = useState("");

  useEffect(() => {
    if (canSettle) UsersAPI.getAll().then((u) => setUsers(u.filter((x) => x.isActive))).catch(() => {});
  }, [canSettle]);

  const addManual = async () => {
    if (!fUser || !(Number(fAmount) > 0)) { alert("Выберите сотрудника и укажите сумму"); return; }
    setBusy(true);
    try {
      await CashDebtsAdminAPI.manual(Number(fUser), Number(fAmount), fNote);
      setFormOpen(false); setFUser(""); setFAmount(""); setFNote("");
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Не удалось записать долг");
    } finally {
      setBusy(false);
    }
  };

  const cancelItem = async (id: number) => {
    if (!window.confirm("Отменить эту запись? Используйте, если она внесена по ошибке.")) return;
    try { await CashDebtsAdminAPI.cancel(id); await load(); } catch (e: any) { alert(e?.response?.data?.message ?? "Не удалось отменить"); }
  };

  const load = useCallback(async () => {
    setError("");
    try {
      if (user?.role === "TECHNICIAN" || !seesAll) setMine(await CashDebtsAPI.mine());
      if (seesAll) setStaff(await CashDebtsAPI.summary());
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Не удалось загрузить данные");
    }
  }, [seesAll, user?.role]);

  useEffect(() => { load(); }, [load]);

  const settle = async (s: StaffCashDebt) => {
    if (!window.confirm(`Принять наличные ${money(s.total)} от «${s.name}»? Долг будет закрыт.`)) return;
    setBusy(true);
    try {
      await CashDebtsAPI.settle(s.userId);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Не удалось закрыть долг");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 1000, color: "#e5e7eb" }}>
      <h1 style={{ fontSize: 22, margin: "0 0 4px" }}>💵 Касса / Долги по наличным</h1>
      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
        Наличные, принятые техником от клиента, числятся за ним, пока он не сдаст их в кассу.
      </div>
      {error && <div style={{ color: "#f87171", marginBottom: 12 }}>{error}</div>}

      {canSettle && (
        <div style={{ marginBottom: 14 }}>
          <button style={{ ...btn, background: "#6366f1" }} onClick={() => setFormOpen((o) => !o)}>
            {formOpen ? "✕ Закрыть форму" : "+ Записать долг вручную"}
          </button>
          {formOpen && (
            <div style={{ ...card, marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Если сотрудник принял наличные, а в системе этого нет (например, по старой сделке).</div>
              <select value={fUser} onChange={(e) => setFUser(e.target.value ? Number(e.target.value) : "")}
                style={{ padding: "9px 10px", borderRadius: 8, background: "#1a2030", color: "#f9fafb", border: "1px solid #374151" }}>
                <option value="">— сотрудник —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.lastName} {u.firstName}</option>)}
              </select>
              <input type="number" inputMode="decimal" placeholder="Сумма, ₸" value={fAmount} onChange={(e) => setFAmount(e.target.value)}
                style={{ padding: "9px 10px", borderRadius: 8, background: "#1a2030", color: "#f9fafb", border: "1px solid #374151" }} />
              <input placeholder="Комментарий (например, № сделки)" value={fNote} onChange={(e) => setFNote(e.target.value)}
                style={{ padding: "9px 10px", borderRadius: 8, background: "#1a2030", color: "#f9fafb", border: "1px solid #374151" }} />
              <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={addManual}>Записать</button>
            </div>
          )}
        </div>
      )}

      {mine && (
        <div style={{ ...card, marginBottom: 18 }}>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Ваш долг по наличным</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: mine.total > 0 ? "#f59e0b" : "#10b981" }}>{money(mine.total)}</div>
          {mine.total > 0 && <div style={{ fontSize: 12, color: "#9ca3af" }}>Сдайте наличные в кассу — руководитель или бухгалтер отметит это здесь.</div>}
          {mine.open.length > 0 && (
            <table style={{ width: "100%", marginTop: 10, fontSize: 13, borderCollapse: "collapse" }}>
              <tbody>
                {mine.open.map((i) => (
                  <tr key={i.id} style={{ borderTop: "1px solid #1f2937" }}>
                    <td style={{ padding: "6px 0" }}>{fmt(i.createdAt)}</td>
                    <td>{i.dealNumber ?? "—"} {i.dealTitle ? `· ${i.dealTitle}` : ""}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{money(i.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {mine.history.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", color: "#9ca3af", fontSize: 13 }}>Сданные ранее ({mine.history.length})</summary>
              {mine.history.map((i) => (
                <div key={i.id} style={{ fontSize: 12, color: "#6b7280", padding: "3px 0" }}>
                  {fmt(i.settledAt)} — {money(i.amount)} ({i.dealNumber ?? "—"}) принял: {i.settledBy ?? "—"}
                </div>
              ))}
            </details>
          )}
        </div>
      )}

      {staff && (
        <>
          <div style={{ ...card, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "#9ca3af" }}>Всего не сдано в кассу</span>
            <b style={{ fontSize: 22, color: staff.grandTotal > 0 ? "#f59e0b" : "#10b981" }}>{money(staff.grandTotal)}</b>
          </div>
          {staff.staff.length === 0 && <div style={{ color: "#6b7280" }}>Долгов нет — все наличные сданы.</div>}
          {staff.staff.map((s) => (
            <div key={s.userId} style={{ ...card, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {s.openCount > 0 ? `${s.openCount} платежей, самый старый ${days(s.oldest)} дн. назад` : "долгов нет"}
                  </div>
                </div>
                <b style={{ fontSize: 18, color: s.total > 0 ? "#f59e0b" : "#10b981" }}>{money(s.total)}</b>
                <button style={{ ...btn, background: "transparent", border: "1px solid #374151", color: "#9ca3af" }} onClick={() => setOpenUser(openUser === s.userId ? null : s.userId)}>
                  {openUser === s.userId ? "Скрыть" : "Детали"}
                </button>
                {canSettle && s.total > 0 && (
                  <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => settle(s)}>
                    ✓ Принял в кассу
                  </button>
                )}
              </div>
              {openUser === s.userId && (
                <div style={{ marginTop: 10 }}>
                  {s.open.map((i) => (
                    <div key={i.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderTop: "1px solid #1f2937" }}>
                      <span>{fmt(i.createdAt)} · {i.dealNumber ?? "—"} {i.dealTitle ?? ""}{i.note ? ` · ${i.note}` : ""}</span>
                      <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <b>{money(i.amount)}</b>
                        {canSettle && <button onClick={() => cancelItem(i.id)} title="Отменить запись" style={{ background: "none", border: "1px solid #374151", color: "#f87171", borderRadius: 6, cursor: "pointer", padding: "0 8px" }}>✕</button>}
                      </span>
                    </div>
                  ))}
                  {s.history.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                      Последние сданные:
                      {s.history.slice(0, 5).map((i) => (
                        <div key={i.id}>{fmt(i.settledAt)} — {money(i.amount)} · принял: {i.settledBy ?? "—"}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
